'use client';
/**
 * LE CATALOGUE À PLAT — tous les examens, toutes leurs colonnes.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Demande de la RQ, 26/08/2026 : « je voudrais voir tous mes examens dans un
 * grand tableau avec toutes les colonnes reliées à l'examen. »
 *
 * ── Pourquoi une vue de plus ────────────────────────────────────────────────
 *
 * La liste ordinaire montre huit colonnes et ouvre une fiche au clic : elle
 * répond à « où en est CET examen ? ». Elle ne répond pas à « quels examens
 * n'ont pas de délai de rendu ? », « lesquels sont sous-traités sans prix ? »,
 * « combien annoncent une accréditation sans date ? » — des questions qui se
 * lisent d'un balayage vertical et qu'aucun panneau de détail ne peut servir.
 *
 * ── Le téléchargement vit ICI, et seulement ici ─────────────────────────────
 *
 * L'export CSV était côté Manuel, à côté du livret PDF. La RQ l'a déplacé le
 * 26/08 : « le PDF c'est très bien côté manuel, car c'est pour les confrères ;
 * le CSV, enlève-le totalement du manuel, je veux que ce soit dans Qualité ».
 * C'est juste — le manuel est une VITRINE ; le tableur est un outil de
 * paramétrage, et le paramétrage a déménagé dans Qualité.
 *
 * ⚠️ L'export est celui « de référence » (celui du socle `<Tableau>`, L10), et il ne
 * rend PAS ce que le tableau affiche : il sort toujours TOUTES les colonnes,
 * quelle que soit la bascule « essentielles / toutes », sur les lignes
 * filtrées, avec la date, l'auteur et les filtres en tête. L'ancien export
 * « fidèle à l'écran » est supprimé (décision du 06/10/2026) : un fichier ouvert
 * trois semaines plus tard, hors contexte, ne doit pas dépendre de la façon dont
 * la personne avait réglé son écran ce jour-là.
 */
import React, { useMemo, useState } from 'react';
import { Tableau, ligneOuvrable, type ColonneTriable } from '@/shared/Tableau';
import { formaterDelai, formaterJours } from '@/shared/delaiRendu';

import { Bouton } from '@/shared/Bouton';
import { fmtDate } from '@/lib/meta';
import { useGed } from '@/lib/store';
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type Any = any;

/** Une colonne de la vue à plat. `texte` sert à la fois à l'affichage et au
 *  CSV : deux fonctions distinctes finiraient par ne pas dire la même chose. */
type Colonne = {
  cle: string;
  libelle: string;
  texte: (f: Any) => string;
  /** Colonne large — un texte long, tronqué à l'écran et entier au CSV. */
  longue?: boolean;
  /** Hors du jeu « essentiel » : visible seulement en mode « toutes ». */
  detail?: boolean;
  /**
   * D'OÙ VIENT L'INFORMATION — la question de la RQ, 26/08/2026 : « grâce à un
   * code couleur des libellés, je veux savoir si l'info vient du paramètre ou
   * bien de l'examen. »
   *
   * Ce n'est pas un détail d'affichage : c'est ce qui dit OÙ ALLER CORRIGER une
   * cellule vide. Une colonne « Unité » vide ne se remplit pas sur la fiche de
   * l'examen — elle se remplit au référentiel des paramètres, un écran plus
   * loin. Sans la couleur, une cellule vide se lit comme un oubli de saisie sur
   * l'examen, et on la cherche là où elle n'est pas.
   */
  source: 'examen' | 'parametre';
};

/** Les valeurs distinctes d'un champ, sur les paramètres de l'examen. Un examen
 *  à trois paramètres qui rendent tous des UI/L affiche « UI/L », pas trois
 *  fois la même chose ; un examen qui mêle les unités les montre TOUTES —
 *  c'est précisément le cas qu'il faut voir. */
function desParametres(f: Any, champ: string): string {
  const vus = new Set<string>();
  for (const a of (f?.analytes || []) as Any[]) {
    const v = txt(a?.[champ]);
    if (v) vus.add(v);
  }
  return [...vus].join(' · ');
}

/**
 * LE CODE COULEUR DES LIBELLÉS — d'où vient l'information.
 *
 * Deux couleurs, pas trois : la question posée est binaire (« du paramètre ou
 * bien de l'examen ? »), et une troisième nuance ferait chercher ce qu'elle
 * veut dire à chaque lecture.
 *
 * ⚠️ La couleur ne porte JAMAIS seule le sens : chaque en-tête porte aussi une
 * puce (▪ / ▫) et un `title`. Un daltonisme deutan confond le cyan et le vert
 * olive à cette taille, et un tableau imprimé en noir et blanc perd la couleur
 * entièrement — or ce tableau est fait pour être exporté et relu ailleurs.
 */
const SOURCES = {
  examen: { couleur: 'var(--cyan-700)', puce: '▪', mot: 'examen',
    aide: 'Champ de la fiche de l’examen — se corrige en ouvrant l’examen.' },
  parametre: { couleur: 'var(--violet-600)', puce: '▫', mot: 'paramètre',
    aide: 'Champ du référentiel des paramètres — se corrige à l’onglet « Paramètres », pas sur l’examen.' },
} as const;

const oui = (v: unknown) => (v ? 'oui' : '');
const liste = (v: unknown) => (Array.isArray(v) ? v.filter(Boolean).join(' · ') : String(v ?? ''));
const txt = (v: unknown) => String(v ?? '').replace(/\s+/g, ' ').trim();

/**
 * LES COLONNES, dans l'ordre où on lit une fiche : ce qu'elle est, ce qu'on
 * prélève, quand on la rend, ce qu'elle coûte, ce qu'elle affirme.
 *
 * L'ordre n'est pas décoratif : c'est celui du dossier de la fiche. Quelqu'un
 * qui vérifie une ligne du tableau doit retrouver les champs dans le même ordre
 * en ouvrant l'examen, sinon il vérifie à l'aveugle.
 */
const COLONNES: Colonne[] = [
  { source: 'examen', cle: 'code', libelle: 'Code', texte: (f) => txt(f.code) },
  { source: 'examen', cle: 'libelle', libelle: 'Examen', texte: (f) => txt(f.libelle) },
  { source: 'examen', cle: 'version', libelle: 'Version', texte: (f) => txt(f.version) },
  { source: 'examen', cle: 'famille', libelle: 'Famille', texte: (f) => txt(f.famille) },
  { source: 'examen', cle: 'sousFamille', libelle: 'Sous-famille', texte: (f) => txt(f.sousFamille) },
  /* Le libellé vient du MOTEUR, jamais d'une traduction locale : c'est
     exactement ce qui donnait « Sur place » sur la fiche et « place » ici
     (26/08). Le repli sur le code brut rend visible une valeur hors liste. */
  { source: 'examen', cle: 'lieu', libelle: 'Lieu', texte: (f) => txt(f.lieuLibelle || f.lieu) },
  { source: 'examen', cle: 'parametres', libelle: 'Paramètres', texte: (f) => (f.analytes || []).map((a: Any) => a.code).join(' · ') },
  { source: 'examen', cle: 'nbParametres', libelle: 'Nb param.', texte: (f) => String((f.analytes || []).length) },
  /* ── CE QUE LE PARAMÈTRE APPORTE ────────────────────────────────────────
     L'unité, la nature du résultat et la discipline ne sont PAS sur l'examen :
     elles vivent au référentiel des paramètres. Les colonnes restaient donc
     vides, et une cellule vide se lit comme un oubli de saisie sur la fiche —
     on la cherchait là où elle n'est pas. */
  { source: 'parametre', cle: 'unite', libelle: 'Unité', texte: (f) => desParametres(f, 'uniteDefaut') },
  { source: 'parametre', cle: 'uniteSecondaire', libelle: 'Seconde unité', texte: (f) => desParametres(f, 'uniteSecondaire'), detail: true },
  { source: 'parametre', cle: 'facteurConversion', libelle: 'Facteur de conversion', texte: (f) => desParametres(f, 'facteurConversion'), detail: true },
  { source: 'parametre', cle: 'typeResultat', libelle: 'Nature du résultat', texte: (f) => desParametres(f, 'typeResultatLibelle') || desParametres(f, 'typeResultat') },
  { source: 'parametre', cle: 'matiereParam', libelle: 'Matière (paramètre)', texte: (f) => desParametres(f, 'matiere') },
  { source: 'parametre', cle: 'discipline', libelle: 'Discipline', texte: (f) => desParametres(f, 'discipline') },
  { source: 'parametre', cle: 'decimales', libelle: 'Décimales', texte: (f) => desParametres(f, 'decimales'), detail: true },
  { source: 'parametre', cle: 'formuleParam', libelle: 'Formule (paramètre)', texte: (f) => desParametres(f, 'formule'), detail: true },
  /* Le NOMBRE de valeurs usuelles adoptées, pas leur contenu : un intervalle
     dépend du sexe et de l'âge, il ne tient pas dans une cellule. Zéro est
     l'information qui compte — c'est le paramètre dont rien n'est adopté. */
  { source: 'parametre', cle: 'valeursUsuelles', libelle: 'Valeurs usuelles (nb)',
    texte: (f) => String(((f?.analytes || []) as Any[])
      .reduce((n, a) => n + (Array.isArray(a?.valeursUsuelles) ? a.valeursUsuelles.length : 0), 0)) },
  { source: 'examen', cle: 'techniques', libelle: 'Techniques', texte: (f) => liste(f.techniques) || String(f.nbTechniques ?? ''), detail: true },
  /* LE PRINCIPE DE MÉTHODE (26/08/2026) — le terme de la portée
     d'accréditation, et le couple technique + automate plutôt que la technique
     seule : chez BIO24 nommer l'automate nomme la technique. Calculé, donc
     jamais en désaccord avec les techniques de la ligne au-dessus. */
  { source: 'examen', cle: 'principeMethode', libelle: 'Principe de méthode', texte: (f) => liste(f.principeMethode), longue: true },
  /* Les matières une par une (26/08) : c'est la 4e dimension de la ligne de
     portée d'accréditation, et elle n'était lisible nulle part à plat. */
  { source: 'examen', cle: 'matieres', libelle: 'Matières', texte: (f) => (f.matrices || []).map((m: Any) => m.echantillon).join(' · ') },
  { source: 'examen', cle: 'contenants', libelle: 'Contenants', texte: (f) => (f.matrices || []).map((m: Any) => m.recipientLibelle || m.recipient || '—').join(' · ') },
  { source: 'examen', cle: 'milieux', libelle: 'Milieux prélevés', texte: (f) => (Array.isArray(f.milieux) ? f.milieux.join(' · ') : ''), detail: true },
  { source: 'examen', cle: 'echantillon', libelle: 'Échantillon', texte: (f) => txt(f.echantillon), detail: true },
  { source: 'examen', cle: 'recipient', libelle: 'Contenant de l’examen', texte: (f) => txt(f.recipientLibelle || f.recipient), detail: true },
  { source: 'examen', cle: 'recipientDero', libelle: 'Dérogation contenant', texte: (f) => txt(f.recipientDero), detail: true },
  { source: 'examen', cle: 'volumeMin', libelle: 'Volume min.', texte: (f) => txt(f.volumeMin) },
  { source: 'examen', cle: 'prep', libelle: 'Préparation', texte: (f) => txt(f.prep), longue: true, detail: true },
  { source: 'examen', cle: 'conditions', libelle: 'Conditions', texte: (f) => txt(f.conditions), longue: true, detail: true },
  { source: 'examen', cle: 'transport', libelle: 'Transport', texte: (f) => txt(f.transport), detail: true },
  { source: 'examen', cle: 'congele', libelle: 'Congelé', texte: (f) => txt(f.congele), detail: true },
  { source: 'examen', cle: 'refrigere', libelle: 'Réfrigéré', texte: (f) => txt(f.refrigere), detail: true },
  { source: 'examen', cle: 'ambiante', libelle: 'Ambiante', texte: (f) => txt(f.ambiante), detail: true },
  { source: 'examen', cle: 'delaiAjout', libelle: 'Délai d’ajout', texte: (f) => txt(f.delaiAjout), detail: true },
  { source: 'examen', cle: 'joursRealisation', libelle: 'Jours de réalisation', texte: (f) => formaterJours(f.joursRealisation) },
  /* DÉLAI DE RENDU — la colonne visible dit la VALEUR AFFICHÉE, formatée par le
     socle comme sur la fiche patient : c'est ce qu'on relit pour vérifier ce
     que le public voit. Les heures brutes restent à côté, en colonne de détail,
     parce que c'est la donnée qui se trie et se somme.
     Depuis la reprise du 19/10/2026, l'ancien texte n'est plus qu'une trace,
     en colonne de détail ; le délai de réalisation en texte libre a disparu,
     remplacé par les jours de réalisation. */
  { source: 'examen', cle: 'delaiRenduAffiche', libelle: 'Délai de rendu', texte: (f) => formaterDelai(f.delaiRenduHeures) },
  { source: 'examen', cle: 'delaiRenduHeures', libelle: 'Délai de rendu (h brutes)', texte: (f) => txt(f.delaiRenduHeures), detail: true },
  { source: 'examen', cle: 'delaiRendu', libelle: 'Délai de rendu (ancien texte)', texte: (f) => txt(f.delaiRendu), detail: true },
  { source: 'examen', cle: 'renseignements', libelle: 'Renseignements', texte: (f) => txt(f.renseignements), longue: true, detail: true },
  { source: 'examen', cle: 'indications', libelle: 'Indications', texte: (f) => txt(f.indications), longue: true, detail: true },
  { source: 'examen', cle: 'principe', libelle: 'Principe', texte: (f) => txt(f.principe), longue: true, detail: true },
  { source: 'examen', cle: 'valeursRef', libelle: 'Valeurs de référence', texte: (f) => txt(f.valeursRef), longue: true, detail: true },
  { source: 'examen', cle: 'nbIntervalles', libelle: 'Intervalles en vigueur', texte: (f) => String(f.nbIntervallesEnVigueur ?? '') },
  { source: 'examen', cle: 'nbCriteres', libelle: 'Critères d’échantillon', texte: (f) => String(f.nbCriteres ?? '') },
  { source: 'examen', cle: 'cotation', libelle: 'Cotation', texte: (f) => txt(f.cotation) },
  { source: 'examen', cle: 'cnam', libelle: 'CNAM', texte: (f) => txt(f.cnam) },
  { source: 'examen', cle: 'sousTraitance', libelle: 'Proposé à la sous-traitance', texte: (f) => txt(f.sousTraitance) },
  { source: 'examen', cle: 'prixST', libelle: 'Prix HT', texte: (f) => txt(f.prixST) },
  { source: 'examen', cle: 'accredite', libelle: 'Accrédité', texte: (f) => txt(f.accredite) },
  { source: 'examen', cle: 'porteeAccred', libelle: 'Portée d’accréditation', texte: (f) => txt(f.porteeAccred), longue: true, detail: true },
  { source: 'examen', cle: 'dateAccred', libelle: 'Date d’accréditation', texte: (f) => (f.dateAccred ? fmtDate(String(f.dateAccred)) : '') },
  { source: 'examen', cle: 'nature', libelle: 'Nature', texte: (f) => txt(f.nature) },
  { source: 'examen', cle: 'formule', libelle: 'Formule', texte: (f) => txt(f.formule), detail: true },
  { source: 'examen', cle: 'genetique', libelle: 'Génétique', texte: (f) => txt(f.genetique), detail: true },
  { source: 'examen', cle: 'affichageTechniques', libelle: 'Affichage techniques', texte: (f) => txt(f.affichageTechniques), detail: true },
  { source: 'examen', cle: 'synonymes', libelle: 'Synonymes', texte: (f) => liste(f.synonymes), detail: true },
  { source: 'examen', cle: 'cibles', libelle: 'Cibles', texte: (f) => liste(f.cibles), longue: true, detail: true },
  { source: 'examen', cle: 'remarqueImportante', libelle: 'Remarque importante', texte: (f) => txt(f.remarqueImportante), longue: true, detail: true },
  { source: 'examen', cle: 'versionLe', libelle: 'Version le', texte: (f) => (f.versionLe ? fmtDate(String(f.versionLe)) : ''), detail: true },
  { source: 'examen', cle: 'versionPar', libelle: 'Version par', texte: (f) => txt(f.versionPar), detail: true },
  { source: 'examen', cle: 'archivee', libelle: 'Archivé', texte: (f) => oui(f.archivee) },
];

export function CataloguePlat({ lignes, filtre, onOuvrir }: {
  lignes: Any[];
  /** Le même filtre que la liste ordinaire — l'écran n'a qu'une recherche. */
  filtre: string;
  onOuvrir: (documentId: string) => void;
}) {
  const [toutes, setToutes] = useState(false);
  const [archives, setArchives] = useState(false);

  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    return (lignes || [])
      .filter((f) => archives || !f.archivee)
      .filter((f) => !q
        || String(f.code || '').toLowerCase().includes(q)
        || String(f.libelle || '').toLowerCase().includes(q)
        || String(f.famille || '').toLowerCase().includes(q)
        || (f.analytes || []).some((a: Any) => String(a.code || '').toLowerCase().includes(q)))
      .sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [lignes, filtre, archives]);

  const auteur = useGed((st) => st.session.name || st.session.key);

  /* L10 (11/10/2026) : la vue à plat est la VUE D'EXPORT DE RÉFÉRENCE du catalogue,
     rendue par le socle au niveau AVANCÉ. Chaque colonne du gabarit devient une
     colonne du tableau : `valeur` = le texte (c'est aussi ce qui s'exporte),
     « Code » et « Examen » épinglés (on lit « oui / 24 / EDTA » sans savoir de quel
     examen il s'agit, sinon), la source dite par la couleur, la puce ET un texte
     pour les lecteurs d'écran. L'EXPORT sort TOUTES les colonnes (`export.colonnes`)
     quelle que soit la bascule « essentielles / toutes », qui n'est qu'une
     préférence d'affichage. */
  const toutesColonnes = useMemo<ColonneTriable<Any>[]>(() => COLONNES.map((c, i) => ({
    cle: c.cle,
    titre: `${c.libelle} (${SOURCES[c.source].mot})`,
    libelle: (
      <span title={`${c.libelle} — ${SOURCES[c.source].aide}`} style={{ color: SOURCES[c.source].couleur }}>
        <span aria-hidden style={{ marginInlineEnd: 4 }}>{SOURCES[c.source].puce}</span>
        {c.libelle}
        <span style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0 }}>
          {' '}(source : {SOURCES[c.source].mot})
        </span>
      </span>
    ),
    valeur: c.texte,
    export: true,
    ...(i < 2 ? { epinglee: true, obligatoire: true } : {}),
    style: { maxWidth: c.longue ? 260 : 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: i === 0 ? 700 : 400 },
    rendu: (f: Any) => { const v = c.texte(f); return <span title={v.length > 40 ? v : undefined} style={{ color: v ? undefined : 'var(--text-faint)' }}>{v || '—'}</span>; },
  })), []);
  const colonnes = useMemo(() => (toutes ? toutesColonnes : toutesColonnes.filter((_, i) => !COLONNES[i].detail)), [toutes, toutesColonnes]);

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          <strong style={{ color: 'var(--text-strong)' }}>{visibles.length}</strong> examen(s) ·{' '}
          {colonnes.length} colonnes
        </span>
        <Bouton variante={toutes ? 'primaire' : 'contour'} taille="sm" type="button" aria-pressed={toutes} onClick={() => setToutes((v) => !v)}>
          {toutes ? 'Colonnes essentielles' : `Toutes les colonnes (${COLONNES.length})`}
        </Bouton>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: 'var(--text-body)' }}>
          <input type="checkbox" checked={archives} onChange={(e) => setArchives(e.target.checked)} />
          Inclure les archivés
        </label>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
        {(['examen', 'parametre'] as const).map((k) => (
          <span key={k} style={{ display: 'inline-flex', gap: 5, alignItems: 'baseline', color: 'var(--text-muted)' }}>
            <span aria-hidden style={{ color: SOURCES[k].couleur, fontWeight: 700 }}>{SOURCES[k].puce}</span>
            <span style={{ color: SOURCES[k].couleur, fontWeight: 700 }}>
              {k === 'examen' ? 'Fiche de l’examen' : 'Référentiel des paramètres'}
            </span>
            <span>
              {k === 'examen'
                ? `${COLONNES.filter((c) => c.source === 'examen').length} colonnes — se corrigent en ouvrant l’examen`
                : `${COLONNES.filter((c) => c.source === 'parametre').length} colonnes — se corrigent à l’onglet « Paramètres »`}
            </span>
          </span>
        ))}
      </div>

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '92ch' }}>
        Un clic sur une ligne ouvre l’examen. Les textes longs sont tronqués ici — survolez-les pour
        les lire en entier ; le fichier exporté, lui, porte toutes les colonnes et les textes complets.
      </p>

      <Tableau
        id="catalogue-plat"
        ajusterLargeur
        niveau="avance"
        defilementHorizontal
        hauteurMax={640}
        lignes={visibles}
        cleLigne={(f) => String(f.documentId)}
        ligneProps={(f) => ligneOuvrable(() => onOuvrir(f.documentId), `Ouvrir ${f.code}`)}
        vide={{ titre: 'Aucun examen à afficher.', indice: filtre.trim() ? 'Élargissez la recherche.' : 'Le catalogue est vide.' }}
        colonnes={colonnes}
        export={{
          registre: 'catalogue', libelleRegistre: 'Catalogue des examens', auteur,
          colonnes: toutesColonnes,
          filtres: [
            ...(filtre.trim() ? [`Recherche « ${filtre.trim()} »`] : []),
            archives ? 'Archivés inclus' : 'Archivés exclus',
          ],
        }}
      />
    </div>
  );
}
